import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useAppContext } from '../context/AppContext';
import { calculateForecast, formatCurrency, formatDateShort, getPaymentTypeInfo } from '../utils/calculations';
import { addDays, addWeeks, addMonths, format } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

type TimePeriod = 'days' | 'weeks' | 'months';

const ForecastChart: React.FC = () => {
  const { state } = useAppContext();
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('days');
  const [timeAmount, setTimeAmount] = useState(31);

  const [visibleLines, setVisibleLines] = useState<{[key: string]: boolean}>({
    account: true,
    total: true,
    ...state.pots
      .filter(pot => pot.trackingType === 'cumulative')
      .reduce((acc, pot) => ({ ...acc, [pot.id]: true }), {})
  });

  const forecastData = useMemo(() => {
    let endDate: Date;
    const today = new Date();
    
    switch (timePeriod) {
      case 'days':
        endDate = addDays(today, timeAmount);
        break;
      case 'weeks':
        endDate = addWeeks(today, timeAmount);
        break;
      case 'months':
        endDate = addMonths(today, timeAmount);
        break;
    }

    const totalDays = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    const forecast = calculateForecast(
      state.account,
      state.recurringPayments,
      state.oneOffPayments,
      state.paydayRules,
      state.pots,
      state.creditCards,
      totalDays
    );

    // Calculate pot balances over time (only for cumulative pots)
    const potBalances: {[potId: string]: number[]} = {};
    const cumulativePots = state.pots.filter(pot => pot.trackingType === 'cumulative');
    cumulativePots.forEach(pot => {
      potBalances[pot.id] = [pot.currentBalance]; // Start with current balance
    });

    let currentPotBalances: {[potId: string]: number} = { ...cumulativePots.reduce((acc, pot) => ({ ...acc, [pot.id]: pot.currentBalance }), {}) };

    forecast.forEach(entry => {
      if (entry.type === 'payday') {
        // Find which pots are affected by this payday entry
        state.paydayRules.forEach(rule => {
          if (rule.isActive && entry.date.getDate() === (rule.dayOfMonth === 'LDOM' ? entry.date.getDate() : rule.dayOfMonth)) {
            rule.allocations.forEach(allocation => {
              if (currentPotBalances[allocation.potId] !== undefined) {
                currentPotBalances[allocation.potId] += allocation.amount;
              }
            });
          }
        });
      }
      
      // Record pot balances for this date (only cumulative pots)
      cumulativePots.forEach(pot => {
        if (currentPotBalances[pot.id] !== undefined) {
          potBalances[pot.id].push(currentPotBalances[pot.id]);
        }
      });
    });

    return { forecast, potBalances };
  }, [state, timePeriod, timeAmount]);

  const chartData = useMemo(() => {
    const labels: string[] = [];
    const accountBalances: number[] = [];
    const totalBalances: number[] = [];
    const payments: Array<{date: string, description: string, amount: number}> = [];
    
    // Start with current balances
    const today = new Date();
    labels.push(formatDateShort(today));
    accountBalances.push(state.account.currentBalance);
    
    const initialPotTotal = state.pots
      .filter(pot => pot.trackingType === 'cumulative')
      .reduce((sum, pot) => sum + pot.currentBalance, 0);
    totalBalances.push(state.account.currentBalance + initialPotTotal);
    
    // Add forecast data
    forecastData.forecast.forEach((entry, index) => {
      labels.push(formatDateShort(entry.date));
      accountBalances.push(entry.balance);
      payments.push({
        date: formatDateShort(entry.date),
        description: entry.description,
        amount: entry.amount
      });
      
      // Calculate total value (account + cumulative pots only)
      const potTotal = state.pots
        .filter(pot => pot.trackingType === 'cumulative')
        .reduce((sum, pot) => {
          const potBalance = forecastData.potBalances[pot.id]?.[index + 1] || pot.currentBalance;
          return sum + potBalance;
        }, 0);
      totalBalances.push(entry.balance + potTotal);
    });

    const datasets = [];
    
    // Total value line (most prominent)
    if (visibleLines.total) {
      datasets.push({
        label: 'Total Value',
        data: totalBalances,
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointBackgroundColor: 'rgb(34, 197, 94)',
        pointBorderColor: 'rgb(34, 197, 94)',
        pointRadius: 2,
        pointHoverRadius: 5,
      });
    }
    
    // Account balance line
    if (visibleLines.account) {
      datasets.push({
        label: 'Account Balance',
        data: accountBalances,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: 'rgb(59, 130, 246)',
        pointRadius: 2,
        pointHoverRadius: 5,
      });
    }

    // Pot balance lines (only cumulative pots)
    state.pots
      .filter(pot => pot.trackingType === 'cumulative')
      .forEach(pot => {
        if (visibleLines[pot.id] && forecastData.potBalances[pot.id]) {
          datasets.push({
            label: pot.name,
            data: forecastData.potBalances[pot.id],
            borderColor: pot.color,
            backgroundColor: pot.color + '20',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointBackgroundColor: pot.color,
            pointBorderColor: pot.color,
            pointRadius: 2,
            pointHoverRadius: 5,
          });
        }
      });

    return {
      labels,
      datasets,
      payments
    };
  }, [forecastData, state.account.currentBalance, state.pots, visibleLines]);

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll create custom legend
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            return context[0].label;
          },
          label: (context) => {
            const balance = formatCurrency(context.parsed.y);
            const payment = chartData.payments[context.dataIndex - 1]; // -1 because we start with current balance
            
            if (payment && context.dataset.label === 'Account Balance') {
              return [
                `${context.dataset.label}: ${balance}`,
                `Payment: ${payment.description}`,
                `Amount: ${formatCurrency(payment.amount)}`
              ];
            }
            return `${context.dataset.label}: ${balance}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Balance (£)'
        },
        ticks: {
          callback: function(value) {
            return formatCurrency(value as number);
          }
        },
        beginAtZero: false,
        suggestedMin: chartData.datasets.length > 0 ? Math.min(...chartData.datasets.flatMap(d => d.data)) * 0.95 : 0,
        suggestedMax: chartData.datasets.length > 0 ? Math.max(...chartData.datasets.flatMap(d => d.data)) * 1.05 : 100
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  const handleTimePeriodChange = (period: TimePeriod) => {
    setTimePeriod(period);
    // Set reasonable defaults for each period
    switch (period) {
      case 'days':
        setTimeAmount(31);
        break;
      case 'weeks':
        setTimeAmount(12);
        break;
      case 'months':
        setTimeAmount(6);
        break;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Balance Forecast</h2>
        
        {/* Time Period Controls */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex space-x-1">
            <button
              onClick={() => handleTimePeriodChange('days')}
              className={timePeriod === 'days' ? 'btn-primary' : 'btn-secondary'}
            >
              Days
            </button>
            <button
              onClick={() => handleTimePeriodChange('weeks')}
              className={timePeriod === 'weeks' ? 'btn-primary' : 'btn-secondary'}
            >
              Weeks
            </button>
            <button
              onClick={() => handleTimePeriodChange('months')}
              className={timePeriod === 'months' ? 'btn-primary' : 'btn-secondary'}
            >
              Months
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Show next:</span>
            <input
              type="number"
              min="1"
              max={timePeriod === 'days' ? 365 : timePeriod === 'weeks' ? 52 : 24}
              value={timeAmount}
              onChange={(e) => setTimeAmount(parseInt(e.target.value) || 1)}
              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">{timePeriod}</span>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mb-4 flex justify-center">
          <div className="flex space-x-8">
            <div className="text-center">
              <p className="text-xs text-gray-500" style={{marginBottom: '2px'}}>Current Total</p>
              <p className="text-lg font-bold balance-positive" style={{margin: '0'}}>
                {formatCurrency(state.account.currentBalance + state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => sum + pot.currentBalance, 0))}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500" style={{marginBottom: '2px'}}>Projected Total</p>
              <p className="text-lg font-bold balance-positive" style={{margin: '0'}}>
                {(() => {
                  const currentTotal = state.account.currentBalance + state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => sum + pot.currentBalance, 0);
                  const projectedAccount = forecastData.forecast.length > 0 ? forecastData.forecast[forecastData.forecast.length - 1].balance : state.account.currentBalance;
                  const projectedPotTotal = state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => {
                    const potBalance = forecastData.potBalances[pot.id]?.[forecastData.potBalances[pot.id].length - 1] || pot.currentBalance;
                    return sum + potBalance;
                  }, 0);
                  return formatCurrency(projectedAccount + projectedPotTotal);
                })()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500" style={{marginBottom: '2px'}}>Growth (£)</p>
              <p className={`text-lg font-bold ${(() => {
                const currentTotal = state.account.currentBalance + state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => sum + pot.currentBalance, 0);
                const projectedAccount = forecastData.forecast.length > 0 ? forecastData.forecast[forecastData.forecast.length - 1].balance : state.account.currentBalance;
                const projectedPotTotal = state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => {
                  const potBalance = forecastData.potBalances[pot.id]?.[forecastData.potBalances[pot.id].length - 1] || pot.currentBalance;
                  return sum + potBalance;
                }, 0);
                const projectedTotal = projectedAccount + projectedPotTotal;
                const growth = projectedTotal - currentTotal;
                return growth >= 0 ? 'text-green-600' : 'text-red-600';
              })()}`} style={{margin: '0'}}>
                {(() => {
                  const currentTotal = state.account.currentBalance + state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => sum + pot.currentBalance, 0);
                  const projectedAccount = forecastData.forecast.length > 0 ? forecastData.forecast[forecastData.forecast.length - 1].balance : state.account.currentBalance;
                  const projectedPotTotal = state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => {
                    const potBalance = forecastData.potBalances[pot.id]?.[forecastData.potBalances[pot.id].length - 1] || pot.currentBalance;
                    return sum + potBalance;
                  }, 0);
                  const projectedTotal = projectedAccount + projectedPotTotal;
                  const growth = projectedTotal - currentTotal;
                  return growth >= 0 ? `+${formatCurrency(growth)}` : formatCurrency(growth);
                })()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500" style={{marginBottom: '2px'}}>Growth (%)</p>
              <p className={`text-lg font-bold ${(() => {
                const currentTotal = state.account.currentBalance + state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => sum + pot.currentBalance, 0);
                const projectedAccount = forecastData.forecast.length > 0 ? forecastData.forecast[forecastData.forecast.length - 1].balance : state.account.currentBalance;
                const projectedPotTotal = state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => {
                  const potBalance = forecastData.potBalances[pot.id]?.[forecastData.potBalances[pot.id].length - 1] || pot.currentBalance;
                  return sum + potBalance;
                }, 0);
                const projectedTotal = projectedAccount + projectedPotTotal;
                const growth = projectedTotal - currentTotal;
                return growth >= 0 ? 'text-green-600' : 'text-red-600';
              })()}`} style={{margin: '0'}}>
                {(() => {
                  const currentTotal = state.account.currentBalance + state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => sum + pot.currentBalance, 0);
                  const projectedAccount = forecastData.forecast.length > 0 ? forecastData.forecast[forecastData.forecast.length - 1].balance : state.account.currentBalance;
                  const projectedPotTotal = state.pots.filter(pot => pot.trackingType === 'cumulative').reduce((sum, pot) => {
                    const potBalance = forecastData.potBalances[pot.id]?.[forecastData.potBalances[pot.id].length - 1] || pot.currentBalance;
                    return sum + potBalance;
                  }, 0);
                  const projectedTotal = projectedAccount + projectedPotTotal;
                  const growth = projectedTotal - currentTotal;
                  const growthPercentage = currentTotal > 0 ? (growth / currentTotal) * 100 : 0;
                  return growthPercentage >= 0 ? `+${growthPercentage.toFixed(1)}%` : `${growthPercentage.toFixed(1)}%`;
                })()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500" style={{marginBottom: '2px'}}>Transactions</p>
              <p className="text-lg font-bold text-gray-800" style={{margin: '0'}}>
                {forecastData.forecast.length}
              </p>
            </div>
          </div>
        </div>

        {/* Line Visibility Controls */}
        <div className="mb-3">
          <div className="grid grid-cols-4 gap-1 max-w-md mx-auto">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={visibleLines.total}
                onChange={() => setVisibleLines({...visibleLines, total: !visibleLines.total})}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Total Value</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={visibleLines.account}
                onChange={() => setVisibleLines({...visibleLines, account: !visibleLines.account})}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Account Balance</span>
            </label>
            {state.pots
              .filter(pot => pot.trackingType === 'cumulative')
              .map(pot => {
                return (
                  <label key={pot.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visibleLines[pot.id]}
                      onChange={() => setVisibleLines({...visibleLines, [pot.id]: !visibleLines[pot.id]})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm" style={{ color: pot.color }}>
                      {pot.name}
                    </span>
                  </label>
                );
              })}
          </div>
        </div>

        {/* Chart */}
        <div style={{ height: '500px' }}>
          <Line data={chartData} options={options} />
        </div>
      </div>

      {/* Forecast Table */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          Forecast Details ({forecastData.forecast.length} transactions)
        </h2>
        <div className="table-container dashboard-forecast" style={{maxHeight: '400px', overflowY: 'auto'}}>
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Description</th>
                <th className="text-center">Source</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {forecastData.forecast.map((entry, index) => {
                const paymentTypeInfo = getPaymentTypeInfo(entry);
                return (
                  <tr key={index}>
                    <td className="text-left text-gray-600">{formatDateShort(entry.date)}</td>
                    <td className="text-left text-gray-800">{entry.description}</td>
                    <td className="text-center">
                      <span className={paymentTypeInfo.className}>
                        {paymentTypeInfo.label}
                      </span>
                    </td>
                    <td className={`text-right font-medium ${entry.amount >= 0 ? 'balance-positive' : 'balance-negative'}`}>
                      {formatCurrency(entry.amount)}
                    </td>
                    <td className={`text-right font-medium ${entry.balance >= 0 ? 'balance-positive' : 'balance-negative'}`}>
                      {formatCurrency(entry.balance)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ForecastChart;